import {
  PublicIp,
  PublicIpChange,
  type CreatePublicIpRequest,
  type ReverseDnsRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

const key = (projectId: string) => ["projects", projectId, "public-ips"] as const;

/** The project's public IPs; live updates refresh them through the `projects` key. */
export const usePublicIps = (projectId: string) =>
  useQuery({
    queryKey: key(projectId),
    queryFn: () => api(`/projects/${projectId}/public-ips`, { schema: z.array(PublicIp) }),
  });

/** IP writes also change servers (their addresses), so every project query is refreshed. */
function useIpMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects"] }),
  });
}

const base = (projectId: string) => `/projects/${projectId}/public-ips`;

export const useCreatePublicIp = (projectId: string) =>
  useIpMutation(
    (body: Partial<CreatePublicIpRequest> & Pick<CreatePublicIpRequest, "name" | "family">) =>
      api(base(projectId), {
        method: "POST",
        body,
        schema: PublicIpChange,
        idempotencyKey: crypto.randomUUID(),
      }),
  );

export const useUpdatePublicIp = (projectId: string) =>
  useIpMutation(({ id, ...body }: { id: string; name?: string; autoDelete?: boolean }) =>
    api(`${base(projectId)}/${id}`, { method: "PATCH", body, schema: PublicIp }),
  );

const useIpAction = <B>(projectId: string, name: string) =>
  useIpMutation(({ id, body }: { id: string; body?: B }) =>
    api(`${base(projectId)}/${id}/actions/${name}`, {
      method: "POST",
      ...(body === undefined ? {} : { body }),
      schema: PublicIpChange,
    }),
  );

export const useAssignPublicIp = (projectId: string) =>
  useIpAction<{ serverId: string }>(projectId, "assign");
export const useUnassignPublicIp = (projectId: string) => useIpAction<never>(projectId, "unassign");
export const useReverseDns = (projectId: string) =>
  useIpAction<ReverseDnsRequest>(projectId, "reverse-dns");
export const useIpProtection = (projectId: string) =>
  useIpAction<{ delete: boolean }>(projectId, "protection");

export const useDeletePublicIp = (projectId: string) =>
  useIpMutation((id: string) => api(`${base(projectId)}/${id}`, { method: "DELETE" }));
